import { useEffect, useState } from 'react';
import { NotificationAlert } from '@/data/notifications';
import { HEALTH_STATES, MIME_TYPE } from '@/const';
import { useLazyGetResourceQuery } from '@/store/api/diagnosticApi';
import { useTranslation } from 'react-i18next';
import { colors, HealthSeverity } from '@/constants/colors';
import { Dialog, DialogContent, DialogHeader } from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/hooks/shared/use-toast';

const renderAttachment = (type: string, attachment: string) => {
  switch (type) {
    case MIME_TYPE.Image_JPG:
      return <img src={`data:${type};base64, ${attachment}`} alt="attachment" />;
    case MIME_TYPE.Image_PNG:
      return <img src={`data:${type};base64, ${attachment}`} alt="attachment" />;
    case MIME_TYPE.Video_MP4:
      return (
        <video controls autoPlay loop>
          <source type={type} src={`data:${type};base64, ${attachment}`} />
        </video>
      );
    default:
      return null;
  }
};

function GalleryActiveAttachment(props: { fetching: boolean; resources: any[]; activeResource: any }) {
  return (
    <>
      {!props.fetching && props.resources.length ? (
        <div>
          {renderAttachment(
            props.resources[props.activeResource]?.type,
            props.resources[props.activeResource]?.content
          )}
        </div>
      ) : (
        <div className="flex justify-center items-center py-12">
          <Spinner size="lg" />
        </div>
      )}
    </>
  );
}

const NotificationModal = ({
  isOpen,
  onClose,
  notification,
}: {
  isOpen: boolean;
  onOpen?: () => void;
  onClose: () => void;
  notification: NotificationAlert;
}) => {
  const { t } = useTranslation();
  const state = HEALTH_STATES[notification.severity];
  const IconComponent = state?.icon;
  const [fetchResource, { data, isLoading, isFetching }] = useLazyGetResourceQuery();
  const [resources, setResources] = useState<any[]>([]);
  const [activeResource, setActiveResource] = useState<any>(0);

  useEffect(() => {
    const array: any[] = [];

    if (isOpen) {
      notification.attachments.forEach((attachment) => {
        const promise = fetchResource(
          {
            resource_id: attachment.content,
          },
          false
        )
          .unwrap()
          .catch(() => {
            toast({
              variant: 'destructive',
              title: `Failed load notification attachment`,
              description: t('general.errors.communication'),
            });
          });

        array.push(promise);
      });

      const resourcesArray: any[] = [];

      array.forEach(async (promise) => {
        const result = await promise;
        resourcesArray.push(result);
      });
      setResources(resourcesArray);
    }
  }, [isOpen, notification]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="pb-4 rounded-2xl max-w-6xl">
        <DialogHeader className="font-normal text-base">
          <div className="flex items-center gap-2">
            <div className="flex justify-center items-center w-10 h-10 rounded-full bg-gray-100">
              <IconComponent className={`text-[${colors.health[notification.severity as HealthSeverity]}] w-6 h-6`} />
            </div>
            <div className="font-semibold">
              {notification.asset_ui_name}
              {notification.component_name ? ` / ${notification.component_name}` : null} -{' '}
              {notification.event_ui_name}
            </div>
          </div>
        </DialogHeader>
        <div className="px-6 py-2">
          <div className="pb-4">{notification?.content}</div>

          {notification.attachments.length ? (
            <GalleryActiveAttachment fetching={isFetching} resources={resources} activeResource={activeResource} />
          ) : null}

          {!isFetching ? (
            <div className="flex items-center gap-2">
              {resources.length &&
                resources.map((resource, index) => {
                  return (
                    <div
                      key={index}
                      className={`max-w-[100px] my-2 p-1 cursor-pointer ${index === activeResource ? 'border-2 border-black' : ''}`}
                      onClick={() => {
                        setActiveResource(index);
                      }}
                    >
                      {renderAttachment(resource.type, resource.content)}
                    </div>
                  );
                })}
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationModal;
